import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { Loader2, TrendingUp, MessageSquare, DollarSign } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

/**
 * Mini SVG sparkline. Zero-config: hands it {data:[{label,value}]}, returns
 * a compact 400x60 chart with area fill.
 */
function Sparkline({ data, color = "#0f172a", height = 60, width = 480 }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(1, ...data.map((d) => d.value));
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - (d.value / max) * (height - 6) - 3;
    return { x, y, value: d.value, label: d.label };
  });
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
  const area = `${path} L ${width} ${height} L 0 ${height} Z`;
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} className="overflow-visible">
      <path d={area} fill={color} fillOpacity="0.1" />
      <path d={path} fill="none" stroke={color} strokeWidth="2" />
      {points.map((p) => (
        <circle key={p.x} cx={p.x} cy={p.y} r="2" fill={color}>
          <title>{p.label}: {p.value}</title>
        </circle>
      ))}
    </svg>
  );
}

export default function TenantHealthModal({ tenantId, tenantName, healthScore, onOpenChange }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tenantId) { setHistory(null); return; }
    setLoading(true);
    api.get(`/platform/tenants/${tenantId}/health-history`)
      .then((r) => setHistory(r.data))
      .catch(() => setHistory({ days: [] }))
      .finally(() => setLoading(false));
  }, [tenantId]);

  const days = history?.days || [];
  const totalSms = days.reduce((s, d) => s + d.sms, 0);
  const totalRev = days.reduce((s, d) => s + d.revenue, 0);
  const activeDays = days.filter((d) => d.sms > 0 || d.revenue > 0).length;
  const scoreColor = healthScore >= 80 ? "#059669" : healthScore >= 50 ? "#d97706" : "#dc2626";

  return (
    <Dialog open={!!tenantId} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="tenant-health-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{tenantName}</DialogTitle>
          <DialogDescription>
            30-day activity trend. Score is weighted: SMS (30) + revenue (30) + appointments (20) + recency (20).
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-16 flex items-center justify-center text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading history…
          </div>
        ) : (
          <>
            {/* Score + summary strip */}
            <div className="grid grid-cols-4 gap-3 py-2">
              <div className="rounded-lg border border-border p-3">
                <div className="font-mono-label text-[0.55rem] tracking-widest text-muted-foreground">HEALTH</div>
                <div className="font-display text-3xl font-bold tabular-nums" style={{ color: scoreColor }}>{healthScore ?? "—"}</div>
              </div>
              <div className="rounded-lg border border-border p-3">
                <div className="font-mono-label text-[0.55rem] tracking-widest text-muted-foreground">SMS 30D</div>
                <div className="font-display text-3xl font-bold tabular-nums text-navy">{totalSms}</div>
              </div>
              <div className="rounded-lg border border-border p-3">
                <div className="font-mono-label text-[0.55rem] tracking-widest text-muted-foreground">REVENUE 30D</div>
                <div className="font-display text-3xl font-bold tabular-nums text-emerald-700">{money(totalRev)}</div>
              </div>
              <div className="rounded-lg border border-border p-3">
                <div className="font-mono-label text-[0.55rem] tracking-widest text-muted-foreground">ACTIVE DAYS</div>
                <div className="font-display text-3xl font-bold tabular-nums text-navy">{activeDays}/30</div>
              </div>
            </div>

            {/* SMS sparkline */}
            <div className="mt-2 border-t border-border pt-3">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="w-4 h-4 text-muted-foreground" />
                <div className="font-mono-label text-[0.65rem] tracking-widest text-muted-foreground">DAILY SMS SENT</div>
              </div>
              <Sparkline data={days.map((d) => ({ label: d.date, value: d.sms }))} color="#0f172a" />
            </div>

            {/* Revenue sparkline */}
            <div className="mt-2 border-t border-border pt-3">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-4 h-4 text-muted-foreground" />
                <div className="font-mono-label text-[0.65rem] tracking-widest text-muted-foreground">DAILY REVENUE RECOVERED</div>
              </div>
              <Sparkline data={days.map((d) => ({ label: d.date, value: d.revenue }))} color="#059669" />
            </div>

            {activeDays < 7 && (
              <div className="mt-4 rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-sm text-amber-900">
                <TrendingUp className="w-4 h-4 inline mr-1" />
                Only {activeDays} active day{activeDays === 1 ? "" : "s"} in the last 30. Consider a nudge SMS from the tenant row.
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
